import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import katex from 'katex';
import { DEFAULT_NUMBER_SETS_VENN_PARAMS, NumberSetConfig, NumberSetsVennParams } from './number-sets-venn.model';
import {
  VIEWBOX_HEIGHT,
  VIEWBOX_WIDTH,
  computeEllipseGeometries,
  computeExamplesPosition,
  computeLabelPosition,
  hexToRgba,
} from './number-sets-venn.util';

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html' });
}

/** Pre-computed visual state for a single set in the Venn diagram. */
interface SetState {
  config: NumberSetConfig;
  index: number;
  // Ellipse geometry
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  // Label position
  labelX: number;
  labelY: number;
  // Examples position
  examplesX: number;
  examplesY: number;
  // Visual states
  fill: string;
  stroke: string;
  strokeWidth: number;
  labelColor: string;
  labelHtml: SafeHtml;
  showExamples: boolean;
  examplesHtml: SafeHtml;
}

@Component({
  selector: 'dojo-number-sets-venn',
  standalone: true,
  templateUrl: './number-sets-venn.component.html',
  styleUrl: './number-sets-venn.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {},
})
export class NumberSetsVennComponent {
  private readonly sanitizer = inject(DomSanitizer);

  readonly params = input<NumberSetsVennParams>(DEFAULT_NUMBER_SETS_VENN_PARAMS);
  readonly height = input(280);

  readonly viewBoxWidth = VIEWBOX_WIDTH;
  readonly viewBoxHeight = VIEWBOX_HEIGHT;

  /** Tracks which set the user is hovering over (by index). -1 = none */
  readonly hoveredIndex = signal(-1);

  /** Merged active highlight: forced > hovered */
  private readonly activeHighlightIndex = computed(() => {
    const p = this.params();
    if (p.highlightedSet) {
      const idx = p.sets.findIndex((s) => s.id === p.highlightedSet);
      if (idx >= 0) {
        return idx;
      }
    }
    return this.hoveredIndex();
  });

  /** Pre-computed visual states for all sets, in outermost-first order (for SVG z-order). */
  readonly setStates = computed<SetState[]>(() => {
    const p = this.params();
    const sets = p.sets;
    const geometries = computeEllipseGeometries(sets.length);
    const highlightIdx = this.activeHighlightIndex();
    const hasHighlight = highlightIdx >= 0;

    return sets.map((config, i) => {
      const geom = geometries[i];
      const labelPos = computeLabelPosition(i, geometries);
      const examplesPos = computeExamplesPosition(i, geometries);

      const isTargeted = hasHighlight && i <= highlightIdx;
      const accentColor = hasHighlight ? sets[highlightIdx].accentColor : config.accentColor;

      const fill = isTargeted ? hexToRgba(accentColor, 0.08) : 'var(--number-sets-venn-fill)';
      const stroke = isTargeted ? hexToRgba(accentColor, 0.6) : 'var(--number-sets-venn-stroke)';
      const strokeWidth = isTargeted ? 2 : 1.5;
      const labelColor = isTargeted ? accentColor : 'var(--number-sets-venn-label)';
      const showExamples = isTargeted && p.showExamples !== false;

      const labelHtml = this.sanitizer.bypassSecurityTrustHtml(renderLatex(`\\mathbb{${config.id}}`));

      const examplesHtml = this.sanitizer.bypassSecurityTrustHtml(
        config.examples.map((ex) => renderLatex(ex)).join(''),
      );

      return {
        config,
        index: i,
        cx: geom.cx,
        cy: geom.cy,
        rx: geom.rx,
        ry: geom.ry,
        labelX: labelPos.x,
        labelY: labelPos.y,
        examplesX: examplesPos.x,
        examplesY: examplesPos.y,
        fill,
        stroke,
        strokeWidth,
        labelColor,
        labelHtml,
        showExamples,
        examplesHtml,
      };
    });
  });

  /** Reversed set states for SVG rendering (outermost first for correct z-order). */
  readonly reversedSetStates = computed(() => [...this.setStates()].reverse());

  onMouseEnter(setIndex: number): void {
    if (this.params().hoverEnabled !== false) {
      this.hoveredIndex.set(setIndex);
    }
  }

  onMouseLeave(): void {
    this.hoveredIndex.set(-1);
  }
}
